<template>
  <div style="height:100%;">
    <commodity-analysis
      :Analytics="Analytics"
      :asset="Analytics.assetType"
      :colorTheme="colorTheme"
      :dataNest="dataNest"
      :idChart="idChart"
      :infoCharts="infoCharts"
      :infoLegends="infoLegends"
      :isPrices="isPrices"
      :loadingAll="loadingAll"
      :processData="processData"
      :renderChartSignal="renderChartSignal"
      @onTableFormat="onHoverChart"
    />
  </div>
</template>

<script>
import CommodityAnalysis from "./CommodityAnalysis.vue";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";
import {
  colorCall,
  colorCallAlpha2,
  colorFwdF,
  colorFwdAlpha5,
  colorPut,
  colorPutAlpha2,
  darkCall,
  darkPut,
  darkPutAlpha2,
  darkCallAlpha2,
  darkFwdAlpha5,
  darkFwdF,
  formatM,
  formatPercentSm
} from "@/_core/_helpers/_parametric_tools";

export default {
  components: { CommodityAnalysis },
  name: "CommodityDerivatives",
  props: {
    Analytics: {
      required: true,
      default: defaultAnalytics()
    },
    colorTheme: { required: true, type: Boolean, default: true },
    loadingAll: { required: true, type: Boolean, default: false },
    processData: { required: true },
    renderChartSignal: { required: true, default: 0 },
    idChart: { required: true },
    isPrices: { default: false }
  },
  data() {
    return {
      dataNest: "derivatives",
      infoCharts: [
        {
          label: "",
          nameEvent: "",
          nameData: "",
          colorBdr: "",
          colorBck: "",
          classNameIcon: "",
          show: ""
        }
      ],
      infoLegends: []
    };
  },
  created() {
    this.getInfoCharts();
  },
  computed: {
    isCurrency() {
      // console.log("isCurrency")
      return this.Analytics.assetType === "currency";
    }
  },
  methods: {
    isForwardAllowed(key, keys) {
      if (keys.some(d => d.includes("future"))) {
        return !key.includes("forward");
      }
      return true;
    },
    getInfoCharts() {
      // console.log("getInfoCharts")
      const allUniqueKeys = [];
      const dataUnitConfig = {};
      this.Analytics["rawData"][this.dataNest].forEach(d => {
        const keys = Object.keys(d);
        keys.forEach(key => {
          if (
            !allUniqueKeys.includes(key) &&
            this.isForwardAllowed(key, keys)
          ) {
            const userSelectedAsset = this.Analytics.userSelections
              .selectedAsset[0];
            if (!this.isCurrency) {
              dataUnitConfig["sUnit"] = userSelectedAsset.fk_idcurrency;
              dataUnitConfig["unit"] = userSelectedAsset.fk_idunit;
            } else {
              const currName = userSelectedAsset.currency_name;
              dataUnitConfig["sUnit"] = currName.slice(0, 3);
              dataUnitConfig["unit"] = currName.slice(3);
            }
            dataUnitConfig[`div${this.isPrices ? "P" : "Q"}`] = this.isPrices;

            if (key.startsWith("notional") && !this.isPrices) {
              dataUnitConfig[key] = d[key];
            }
            allUniqueKeys.push(key);
          }
        });
      });

      const isPricesChart = this.isPrices ? "p_" : "q_";
      const chartInfo = allUniqueKeys
        .filter(d => d.startsWith(isPricesChart) && !d.endsWith("Perc"))
        .map(d => {
          const methodPrefix = d
            .replace(isPricesChart, "")
            .split(" ")
            .join("");

          const { label, classNameIcon, colorBck, colorBdr } = this[
            methodPrefix
          ]();

          Object.assign(this.Analytics["userSelections"], {
            [`show${d}`]: true
          });
          return {
            label,
            labelTable: label,
            nameEvent: d.split(" ").join(""),
            elementName: d.split(" ").join(""),
            nameData: d,
            classNameIcon,
            colorBdr,
            colorBck,
            colorBckTable: colorBck,
            unitPerc: `${d}Perc`,
            show: `show${d}`,
            formatF: formatM,
            formatPerc: formatPercentSm,
            isPrices: this.isPrices,
            figureElements: this.isPrices ? "bar" : "stackedBar",
            strokeWidth: this.isPrices ? 2 : 1,
            showText: !this.isPrices,
            typeFigure: this.isPrices
              ? { nameFigure: "gBar", toZero: false }
              : { nameFigure: "multipleStackedBars", toZero: true },
            figureDetails: {
              barWidth: 0.66,
              offset: 0,
              height: 4,
              strokeWidth: 2
            },
            ...dataUnitConfig
          };
        });

      Object.assign(this.infoCharts, chartInfo);
      Object.assign(this.Analytics, {
        [`table${this.isPrices ? "p_" : "q_"}${this.dataNest}`]: chartInfo
      });
      const changeInfoTable = this.Analytics.tablep_derivatives?.concat(
        this.Analytics.tableq_derivatives
      );
      this.$emit("changeInfoTable", changeInfoTable ? changeInfoTable : [{}]);
      this.infoLegends = this.createInfoLegends(this.infoCharts);
    },
    createInfoLegends(infoCharts) {
      // console.log("createInfoLegends")
      return infoCharts.map(chart => ({
        label: chart.label,
        show: chart.show,
        classNameIcon: chart.classNameIcon,
        colorBdr: chart.colorBdr,
        colorBck: chart.colorBck
      }));
    },
    longfuture() {
      // console.log("longfuture")
      return {
        label: "+Fut",
        colorBdr: colorFwdF(this.colorTheme),
        colorBck: colorFwdAlpha5(this.colorTheme),
        classNameIcon: "fa fa-plus"
      };
    },
    shortfuture() {
      // console.log("shortfuture")
      return {
        label: "-Fut",
        colorBdr: darkFwdF(this.colorTheme),
        colorBck: darkFwdAlpha5(this.colorTheme),
        classNameIcon: "fa fa-minus"
      };
    },
    longforward() {
      return {
        label: "+Fut",
        colorBdr: colorFwdF(this.colorTheme),
        colorBck: colorFwdAlpha5(this.colorTheme),
        classNameIcon: "fa fa-plus"
      };
    },
    shortforward() {
      return {
        label: "-Fut",
        colorBdr: darkFwdF(this.colorTheme),
        colorBck: darkFwdAlpha5(this.colorTheme),
        classNameIcon: "fa fa-minus"
      };
    },
    longcall() {
      // console.log("longcall")
      return {
        label: "+Call",
        colorBdr: colorCall,
        colorBck: colorCallAlpha2,
        classNameIcon: "fa fa-chevron-up"
      };
    },
    shortcall() {
      // console.log("shortcall")
      return {
        label: "-Call",
        colorBdr: darkCall,
        colorBck: darkCallAlpha2,
        classNameIcon: "fa fa-chevron-down"
      };
    },
    longput() {
      // console.log("longput")
      return {
        label: "+Put",
        colorBdr: colorPut,
        colorBck: colorPutAlpha2,
        classNameIcon: "fa fa-chevron-up"
      };
    },
    shortput() {
      // console.log("shortput")
      return {
        label: "-Put",
        colorBdr: darkPut,
        colorBck: darkPutAlpha2,
        classNameIcon: "fa fa-chevron-down"
      };
    },
    onHoverChart(val) {
      // console.log("onHoverChart")
      this.$emit("onTableFormat", val);
    }
  }
};
</script>
